import { useEffect, useMemo, useState } from 'react';
import { FormInstance, Select, Spin, Upload } from 'antd';

import { DescriptionImage } from '@components/DescriptionImage/DescriptionImage';
import { TextArea } from '@components/Input';
import {
  CustomDropdown,
  CustomLabel,
  StyledFormItem,
} from '@components/onboarding/form/steps/styles';
import { getUploadLabel } from '@lib/utils/stringHelper';
import {
  ImageContainer,
  StyledCloseIconV,
  StyledUploadButton,
} from '@views/jobs/components/InvitedBox/styles';

import cancelV from '../../../svg/CancleV.svg';
import { questionValidation } from '../hooks/useJobsForm/useJobsForm.helper';

import { UploadBoxContentAttachment } from './common';

const { Option } = Select;

type Props = {
  form: FormInstance;
  loading: boolean;
  question: any;
  uploadedImages: { imgSrc: string; columnId: number }[];
  handleInputChange: HandleJobsFormChange;
  handleFileChange: (
    info,
    listColumnId: number,
    questionLabel: string,
    isMiscAttachment?: boolean
  ) => void;
  handleRemoveImage: (
    itemToRemove: {
      imgSrc: string;
      columnId: number;
    },
    isMiscAttachment?: boolean
  ) => void;
  initialValue: string | string[];
};

export const ApplicationQuestion = ({
  form,
  loading,
  question,
  uploadedImages,
  handleInputChange,
  handleFileChange,
  handleRemoveImage,
  initialValue,
}: Props): JSX.Element => {
  const {
    label,
    type,
    list_column_id: listColumnId,
    validation_rules: {
      responseType,
      isRequired,
      prompt,
      options,
      description_image,
      minimumUploads,
      maximumUploads,
    },
  } = question;
  const uploadedImageCount = useMemo(
    () => uploadedImages.filter((img) => img.columnId === listColumnId)?.length,
    [uploadedImages, listColumnId]
  );
  const uploadDisabled = maximumUploads && uploadedImageCount >= maximumUploads;

  const [wasAttachmentChanged, setWasAttachmentChanged] = useState(false);

  useEffect(() => {
    if (type === 'attachment' && isRequired && wasAttachmentChanged) {
      form.validateFields([label]);
    }
  }, [uploadedImageCount, wasAttachmentChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialValueFormItem = useMemo(() => {
    if (responseType === 'Free text' && typeof initialValue === 'string') {
      return initialValue;
    }
    if (responseType === 'Multiple-choice' && Array.isArray(initialValue)) {
      return initialValue;
    }
    if (
      responseType === 'Single-choice' &&
      typeof initialValue === 'string' &&
      Array.isArray(options) &&
      options.includes(initialValue)
    ) {
      return initialValue;
    }
    return undefined;
  }, [initialValue, options, responseType]);
  return (
    <>
      <CustomLabel
        label={prompt + getUploadLabel(minimumUploads, maximumUploads)}
        requiredSymbol={isRequired}
      />
      <StyledFormItem
        name={label}
        rules={
          isRequired
            ? [
                {
                  message: 'This field is required',
                  validator: (_, value) =>
                    questionValidation(
                      type,
                      value,
                      uploadedImageCount,
                      minimumUploads,
                      maximumUploads
                    ),
                },
              ]
            : []
        }
        initialValue={initialValueFormItem}
      >
        {responseType === 'Free text' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <TextArea
              autofit
              placeholder='Type here'
              onChange={(e) => {
                handleInputChange(form, listColumnId, e.target.value, label);
                if (isRequired) {
                  form.validateFields([label]);
                }
              }}
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={initialValueFormItem}
            />
          </>
        ) : type === 'attachment' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <ImageContainer loading={(loading || false).toString()}>
              {loading ? (
                <Spin />
              ) : (
                uploadedImages
                  .filter((img) => img.columnId === listColumnId) // Filter by customListId
                  .map((item, index) => (
                    <div key={index}>
                      <StyledCloseIconV
                        src={cancelV.src}
                        onClick={() => {
                          handleRemoveImage(item);

                          isRequired && form.validateFields([label]);
                        }}
                      />
                      <img
                        style={{ width: '80px' }}
                        src={item.imgSrc}
                        alt={`Image ${index}`}
                      />
                    </div>
                  ))
              )}
            </ImageContainer>
            <Upload
              showUploadList={false}
              accept='.png,.jpeg,.jpg'
              multiple={true}
              beforeUpload={() => {
                return false; // Prevent default upload behavior
              }}
              onChange={(e) => {
                handleFileChange(e, listColumnId, label);

                isRequired && form.validateFields([label]);
                setWasAttachmentChanged(true);
              }}
              disabled={uploadDisabled}
            >
              <StyledUploadButton disabled={uploadDisabled}>
                <UploadBoxContentAttachment />
              </StyledUploadButton>
            </Upload>
          </>
        ) : responseType === 'Multiple-choice' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <CustomDropdown
              mode='multiple'
              placeholder='Select all that apply'
              onChange={(value) => {
                handleInputChange(form, listColumnId, value, label);
                if (isRequired) {
                  form.validateFields([label]);
                }
              }}
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={initialValueFormItem}
            >
              {options?.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </CustomDropdown>
          </>
        ) : (
          <>
            {/* Single-choice */}
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <CustomDropdown
              placeholder='Select One'
              onChange={(value) => {
                handleInputChange(form, listColumnId, value, label);
                if (isRequired) {
                  form.validateFields([label]);
                }
              }}
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={initialValueFormItem}
            >
              {options?.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </CustomDropdown>
          </>
        )}
      </StyledFormItem>
    </>
  );
};
